import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { resolveImage, formatRM, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2, X, Upload, Loader2 } from "lucide-react";

function ProductForm({ initial, categories, onCancel, onSaved }) {
  const { t, i18n } = useTranslation();
  const zh = !i18n.language?.startsWith("en");
  const defaultCat = initial?.category || categories?.[0]?.slug || "";
  const [form, setForm] = useState({
    name_zh: initial?.name_zh || "",
    name_en: initial?.name_en || "",
    desc_zh: initial?.desc_zh || "",
    desc_en: initial?.desc_en || "",
    category: defaultCat,
    sku: initial?.sku || "",
    price: initial?.price ?? 0,
    promo_price: initial?.promo_price ?? "",
    stock: initial?.stock ?? 0,
    images: initial?.images || [],
    featured: initial?.featured ?? false,
    active: initial?.active ?? true,
  });
  const [imageUrl, setImageUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const inputCls = "w-full px-4 py-2.5 rounded-md border border-[#e5ebeb] focus:border-[#00868e] outline-none bg-white transition-colors duration-200";

  const addImageUrl = () => {
    if (!imageUrl.trim()) return;
    setForm((f) => ({ ...f, images: [...f.images, imageUrl.trim()] }));
    setImageUrl("");
  };
  const removeImage = (i) =>
    setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/product-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, images: [...f.images, data.path] }));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        sku: (form.sku || "").trim() || null,
        price: Number(form.price),
        promo_price: form.promo_price === "" ? null : Number(form.promo_price),
        stock: Number(form.stock),
      };
      if (initial?.id) {
        await api.patch(`/admin/products/${initial.id}`, payload);
      } else {
        await api.post("/admin/products", payload);
      }
      toast.success(t("common.saved"));
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="rounded-lg border border-[#e5ebeb] bg-white p-6 space-y-6" data-testid="product-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-[#4a6e77] mb-1.5">名称 (中)</label>
          <input required className={inputCls} value={form.name_zh} onChange={(e) => setForm({ ...form, name_zh: e.target.value })} data-testid="pf-name-zh" />
        </div>
        <div>
          <label className="block text-xs text-[#4a6e77] mb-1.5">Name (EN)</label>
          <input required className={inputCls} value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} data-testid="pf-name-en" />
        </div>
        <div>
          <label className="block text-xs text-[#4a6e77] mb-1.5">描述 (中)</label>
          <textarea rows={3} className={inputCls} value={form.desc_zh} onChange={(e) => setForm({ ...form, desc_zh: e.target.value })} data-testid="pf-desc-zh" />
        </div>
        <div>
          <label className="block text-xs text-[#4a6e77] mb-1.5">Description (EN)</label>
          <textarea rows={3} className={inputCls} value={form.desc_en} onChange={(e) => setForm({ ...form, desc_en: e.target.value })} data-testid="pf-desc-en" />
        </div>
        <div>
          <label className="block text-xs text-[#4a6e77] mb-1.5">{t("admin.category")}</label>
          <select className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="pf-category">
            {(categories || []).length === 0 && <option value="">{zh ? "暂无分类" : "No categories"}</option>}
            {(categories || []).map((c) => <option key={c.slug} value={c.slug}>{zh ? c.name_zh : c.name_en}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-[#4a6e77] mb-1.5">SKU (可选 / optional, unique)</label>
          <input className={inputCls} placeholder="JL-CR-001" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="pf-sku" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-[#4a6e77] mb-1.5">{t("admin.price")} (RM)</label>
            <input type="number" step="0.01" className={inputCls} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="pf-price" />
          </div>
          <div>
            <label className="block text-xs text-[#4a6e77] mb-1.5">{t("admin.promoPrice")}</label>
            <input type="number" step="0.01" className={inputCls} value={form.promo_price} onChange={(e) => setForm({ ...form, promo_price: e.target.value })} data-testid="pf-promo" />
          </div>
        </div>
        <div>
          <label className="block text-xs text-[#4a6e77] mb-1.5">{t("common.stock")}</label>
          <input type="number" className={inputCls} value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="pf-stock" />
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-[#003645]">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="pf-featured" />
            {t("common.featured")}
          </label>
          <label className="flex items-center gap-2 text-sm text-[#003645]">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="pf-active" />
            {t("common.active")}
          </label>
        </div>
      </div>

      {/* Images */}
      <div>
        <div className="text-xs text-[#4a6e77] mb-2">{t("admin.images")}</div>
        <div className="flex flex-wrap gap-3">
          {form.images.map((im, i) => (
            <div key={i} className="relative h-24 w-24 rounded-md overflow-hidden bg-[#f5f0e8] border border-[#e5ebeb]" data-testid={`pf-image-${i}`}>
              <img src={resolveImage(im)} alt="" className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={() => removeImage(i)}
                className="absolute top-1 right-1 h-5 w-5 rounded-full bg-white text-red-600 flex items-center justify-center"
                data-testid={`pf-image-remove-${i}`}
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <div className="flex-1 flex gap-2">
            <input
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder={t("admin.imagesHint")}
              className={inputCls}
              data-testid="pf-image-url-input"
            />
            <button type="button" onClick={addImageUrl} className="px-4 py-2 rounded-md bg-[#005870] text-white text-sm hover:bg-[#003645] transition-colors duration-200" data-testid="pf-add-image-url">
              {t("admin.addImageUrl")}
            </button>
          </div>
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#e5ebeb] text-sm text-[#003645] hover:border-[#00868e] cursor-pointer">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {t("admin.uploadImage")}
            <input type="file" accept="image/*" className="hidden" onChange={uploadImage} disabled={uploading} data-testid="pf-image-upload-input" />
          </label>
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#005870] text-white font-medium hover:bg-[#003645] transition-colors duration-200 disabled:opacity-60" data-testid="pf-save-btn">
          {saving && <Loader2 size={14} className="animate-spin" />}
          {t("common.save")}
        </button>
        <button type="button" onClick={onCancel} className="px-6 py-2.5 rounded-full border border-[#e5ebeb] text-[#003645] hover:bg-[#e5ebeb]/40 transition-colors duration-200">
          {t("common.cancel")}
        </button>
      </div>
    </form>
  );
}

export default function AdminProducts() {
  const { t, i18n } = useTranslation();
  const zh = !i18n.language?.startsWith("en");
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} for new | product for edit
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/products", { params: { include_inactive: true } })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get("/categories", { params: { include_inactive: true } })
      .then((r) => setCategories(r.data))
      .catch(() => setCategories([]));
  }, []);

  const catLabel = (slug) => {
    const c = categories.find((x) => x.slug === slug);
    if (c) return zh ? c.name_zh : c.name_en;
    return slug; // fallback: show slug for orphaned/legacy categories
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success(t("common.saved"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const counts = items.reduce((acc, p) => { acc[p.category] = (acc[p.category] || 0) + 1; return acc; }, {});
  const filtered = filter ? items.filter((p) => p.category === filter) : items;

  return (
    <div className="p-6 md:p-10" data-testid="admin-products-page">
      <div className="flex items-center justify-between">
        <h1 className="font-serif-cn text-3xl text-[#003645] font-semibold">{t("admin.products")}</h1>
        {!editing && (
          <button
            onClick={() => setEditing({})}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#005870] text-white text-sm font-medium hover:bg-[#003645] transition-colors duration-200"
            data-testid="new-product-btn"
          >
            <Plus size={14} /> {t("admin.newProduct")}
          </button>
        )}
      </div>

      {!editing && (
        <div className="mt-6 flex flex-wrap gap-2" data-testid="admin-products-cat-filter">
          <button
            onClick={() => setFilter("")}
            className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-colors duration-200 ${
              !filter ? "bg-[#005870] text-white border-[#005870]" : "bg-white text-[#003645] border-[#e5ebeb] hover:border-[#00868e]"
            }`}
            data-testid="admin-cat-all"
          >
            {t("products.all")} <span className="ml-1 text-[10px] opacity-70">{items.length}</span>
          </button>
          {categories.map((c) => (
            <button
              key={c.slug}
              onClick={() => setFilter(c.slug)}
              className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-colors duration-200 ${
                filter === c.slug ? "bg-[#005870] text-white border-[#005870]" : "bg-white text-[#003645] border-[#e5ebeb] hover:border-[#00868e]"
              }`}
              data-testid={`admin-cat-${c.slug}`}
            >
              {zh ? c.name_zh : c.name_en} <span className="ml-1 text-[10px] opacity-70">{counts[c.slug] || 0}</span>
            </button>
          ))}
        </div>
      )}

      {editing ? (
        <div className="mt-6">
          <ProductForm
            initial={editing.id ? editing : null}
            categories={categories}
            onCancel={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
          />
        </div>
      ) : (
        <div className="mt-6 overflow-x-auto">
          {loading ? (
            <div className="text-[#4a6e77]">{t("common.loading")}</div>
          ) : (
            <table className="w-full text-sm border-collapse" data-testid="admin-products-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-[#4a6e77]">
                  <th className="py-3 px-4 border-b border-[#e5ebeb]">Product</th>
                  <th className="py-3 px-4 border-b border-[#e5ebeb]">Category</th>
                  <th className="py-3 px-4 border-b border-[#e5ebeb]">Price</th>
                  <th className="py-3 px-4 border-b border-[#e5ebeb]">Stock</th>
                  <th className="py-3 px-4 border-b border-[#e5ebeb]">Flags</th>
                  <th className="py-3 px-4 border-b border-[#e5ebeb]"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} className="hover:bg-[#fdfbf7] transition-colors duration-150" data-testid={`admin-product-row-${p.id}`}>
                    <td className="py-3 px-4 border-b border-[#e5ebeb]">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded overflow-hidden bg-[#f5f0e8] shrink-0">
                          {p.images?.[0] && <img src={resolveImage(p.images[0])} alt="" className="h-full w-full object-cover" />}
                        </div>
                        <div>
                          <div className="font-medium text-[#003645]">{p.name_zh}</div>
                          <div className="text-xs text-[#4a6e77]">{p.name_en}</div>
                          {p.sku && <div className="text-[10px] text-[#00868e] mt-0.5" data-testid={`admin-product-sku-${p.id}`}>SKU: {p.sku}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 border-b border-[#e5ebeb]">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-[#00868e]/10 text-[#00868e]" data-testid={`admin-product-cat-${p.id}`}>
                        {catLabel(p.category)}
                      </span>
                    </td>
                    <td className="py-3 px-4 border-b border-[#e5ebeb]">
                      {p.promo_price ? (
                        <div>
                          <div className="text-[#005870] font-semibold">{formatRM(p.promo_price)}</div>
                          <div className="text-xs line-through text-[#4a6e77]">{formatRM(p.price)}</div>
                        </div>
                      ) : (
                        <div className="text-[#005870] font-semibold">{formatRM(p.price)}</div>
                      )}
                    </td>
                    <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#003645]">{p.stock}</td>
                    <td className="py-3 px-4 border-b border-[#e5ebeb] text-xs">
                      <div className="flex gap-1">
                        {p.featured && <span className="px-2 py-0.5 rounded-full bg-[#005870] text-white">★</span>}
                        <span className={`px-2 py-0.5 rounded-full ${p.active ? "bg-[#00868e]/10 text-[#00868e]" : "bg-red-50 text-red-700"}`}>
                          {p.active ? t("common.active") : t("common.inactive")}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 border-b border-[#e5ebeb]">
                      <div className="flex gap-2">
                        <button onClick={() => setEditing(p)} className="text-[#005870] hover:text-[#00868e]" data-testid={`edit-product-${p.id}`}>
                          <Edit size={14} />
                        </button>
                        <button onClick={() => del(p.id)} className="text-red-600 hover:text-red-700" data-testid={`delete-product-${p.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={6} className="py-8 px-4 text-center text-[#4a6e77]">{t("products.empty")}</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
